import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AccountTypeStepProps {
  role: string;
  setRole: (role: string) => void;
  registrationCode: string;
  setRegistrationCode: (code: string) => void;
  isValid: boolean;
  setIsValid: (valid: boolean) => void;
  error: string;
  setError: (error: string) => void;
  // New props for dojo code functionality
  dojoCode?: string;
  setDojoCode?: (code: string) => void;
  dojoName?: string;
  setDojoName?: (name: string) => void;
  adminType?: 'create' | 'join';
  setAdminType?: (type: 'create' | 'join') => void;
}

const AccountTypeStep: React.FC<AccountTypeStepProps> = ({
  role,
  setRole,
  registrationCode,
  setRegistrationCode,
  isValid,
  setIsValid,
  error,
  setError,
  dojoCode = '',
  setDojoCode,
  dojoName = '',
  setDojoName,
  adminType = 'create',
  setAdminType
}) => {
  useUiLanguage();
  const [isValidating, setIsValidating] = useState(false);

  const validateRegistrationCode = async (code: string) => {
    if (!code.trim()) {
      setIsValid(false);
      setError('');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('validate_registration_code', {
        submitted_code: code.trim()
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError(uiText("Unable to validate code. Please try again."));
        setIsValid(false);
        return;
      }

      const result = data as { valid: boolean; error?: string };

      if (result.valid) {
        setIsValid(true);
        setError('');
      } else {
        setIsValid(false);
        setError(result.error || 'Invalid registration code');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setError(uiText("Unable to validate code. Please try again."));
      setIsValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const validateDojoCode = async (code: string) => {
    if (!code.trim()) {
      setIsValid(false);
      setError('');
      if (setDojoName) setDojoName('');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('validate_dojo_code', {
        submitted_code: code.trim()
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        setError(uiText("Unable to validate code. Please try again."));
        setIsValid(false);
        if (setDojoName) setDojoName('');
        return;
      }

      const result = data as { valid: boolean; error?: string; dojo_name?: string };

      if (result.valid) {
        setIsValid(true);
        setError('');
        console.log('Setting dojo name:', result.dojo_name);
        if (setDojoName) setDojoName(result.dojo_name || '');
      } else {
        setIsValid(false);
        if (setDojoName) setDojoName('');
        setError(result.error || 'Invalid dojo code');
      }
    } catch (error) {
      console.error('Validation error:', error);
      setError(uiText("Unable to validate code. Please try again."));
      setIsValid(false);
      if (setDojoName) setDojoName('');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRegistrationCodeChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setRegistrationCode(upperValue);

    // Reset validation state when code changes
    setIsValid(false);
    setError('');
  };

  const handleDojoCodeChange = (value: string) => {
    const upperValue = value.toUpperCase();
    if (setDojoCode) setDojoCode(upperValue);

    // Reset validation state when code changes
    setIsValid(false);
    setError('');
    if (setDojoName) setDojoName('');
  };

  const handleValidate = () => {
    if (adminType === 'create') {
      validateRegistrationCode(registrationCode);
    } else {
      validateDojoCode(dojoCode);
    }
  };

  const handleRoleChange = (newRole: string) => {
    setRole(newRole);
    // Reset admin validation when switching roles
    if (newRole !== 'admin') {
      setRegistrationCode('');
      if (setDojoCode) setDojoCode('');
      setIsValid(false);
      setError('');
      if (setDojoName) setDojoName('');
    }
  };

  const handleAdminTypeChange = (type: 'create' | 'join') => {
    if (setAdminType) setAdminType(type);
    // Reset validation when switching admin types
    setIsValid(false);
    setError('');
    setRegistrationCode('');
    if (setDojoCode) setDojoCode('');
    if (setDojoName) setDojoName('');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {uiText("Account Type")}</h3>
        <p className="text-sm text-gray-600">
          {uiText("Choose your account type to continue with registration.")}</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label>{uiText("I want to register as:")}</Label>
          <RadioGroup value={role} onValueChange={handleRoleChange} className="mt-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="student" id="student" />
              <Label htmlFor="student">{uiText("Student - Join an existing dojo (requires dojo code)")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="admin" id="admin" />
              <Label htmlFor="admin">{uiText("Admin - Create or manage a dojo")}</Label>
            </div>
          </RadioGroup>
        </div>

        {role === 'admin' && (
          <div className="space-y-4 border-t pt-4">
            <div>
              <Label>{uiText("Admin Type:")}</Label>
              <RadioGroup value={adminType} onValueChange={handleAdminTypeChange} className="mt-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="create" id="create" />
                  <Label htmlFor="create">{uiText("Create a new dojo (14-day free trial)")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="join" id="join" />
                  <Label htmlFor="join">{uiText("Become admin of existing dojo (requires dojo code)")}</Label>
                </div>
              </RadioGroup>
            </div>

            {adminType === 'create' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">{uiText("Ready to create your dojo!")}</p>
                    <p className="text-sm text-green-700">{uiText("You'll get a 14-day free trial to explore all features.")}</p>
                  </div>
                </div>
              </div>
            )}

            {adminType === 'join' && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="dojoCode">{uiText("Dojo Code")}</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    {uiText("Enter the dojo code to become an admin of an existing dojo.")}</p>
                  <div className="flex gap-2">
                    <Input
                      id="dojoCode"
                      type="text"
                      placeholder={uiText("Enter dojo code")}
                      value={dojoCode}
                      onChange={(e) => handleDojoCodeChange(e.target.value)}
                      disabled={isValidating}
                      className={`
                        ${isValid ? 'border-green-500 focus:border-green-500' : ''}
                        ${error ? 'border-red-500 focus:border-red-500' : ''}
                      `}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleValidate}
                      disabled={!dojoCode.trim() || isValidating}
                      className="px-4"
                    >
                      {isValidating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        uiText("Validate")
                      )}
                    </Button>
                  </div>
                </div>

                {/* Validation Status */}
                {isValid && (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    <span>{uiText("Valid dojo code! You'll become admin of:")}{' '}{dojoName}</span>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountTypeStep;